import React, { useEffect, useState } from 'react';
import styles from '../insertsPages.module.css';
import { Tabs } from '../../../Tabs';
import { Chooser } from '../../../Chooser';
import { useUserInput } from '../../../../context/UserInputContext.tsx';
import { Visualiser, VisualiserGroup, VisualiserTextBox } from '../../../Visualiser/index';
import { getInsertsAndCardsProduct, findVariantForProduct } from '../../../../utilities/visualiserVariants.js';

/**
 * CardCustomMessage component allows users to add a message for their card.
 *
 * @param {Object} props - Component props.
 * @returns {import('react').JSX.Element}
 */
export function CardCustomMessage({ ...rest }) {
  const { getTextInput, setTextInput, setVisualiserSelection, clearVisualiserSelection } = useUserInput();

  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedSuggestionGroup, setSelectedSuggestionGroup] = useState(0);
  const [chooserOpen, setChooserOpen] = useState(false);
  const messageAreaName = 'Greetings Card > Free-Form > Front';

  let suggestedMessages = (window?.giftWrapData?.insertsAndCards?.greetingsCardTemplateGroups || []).filter((group) =>
    group.area?.includes(messageAreaName)
  );

  const selectedSuggestions = suggestedMessages?.[selectedSuggestionGroup]?.suggestions;
  const suggestionList = Array.isArray(selectedSuggestions) ? selectedSuggestions : [];

  useEffect(() => {
    const productKey = 'customGreetingsCards';
    const product = getInsertsAndCardsProduct({ productKey });

    // Match the known "Text" variant for greetings cards.
    const variantTitle = 'Text Only';
    const variant = findVariantForProduct({ product, variantTitle, fallbackOption1: 'Text' });

    setVisualiserSelection({
      productKey,
      variant,
      config: {
        templateId: 'CARD_CUSTOM_TEXT_ONLY',
        mapping: {
          text: {
            'cardCustomMessage.front': 'UVFrontText',
            'cardCustomMessage.inside': 'UVInsideText'
          }
        }
      }
    });

    return () => {
      clearVisualiserSelection();
    };
  }, [setVisualiserSelection, clearVisualiserSelection]);

  return (
    <div className={styles['visualiser-stack']}>
      <div className={styles['page--container']}>
        <Tabs
          tabs={[
            { id: 'front', label: 'Front' },
            { id: 'inside', label: 'Inside' },
            { id: 'back', label: 'Back' }
          ]}
          activeIndex={activeIndex}
          onChange={setActiveIndex}
          fullWidth={true}
        />

        {activeIndex === 0 && (
          <Visualiser type="GREETINGS_CARD" background="var(--color-white)" subtitle="Tap to edit">
            <VisualiserTextBox
              size={{
                width: '200px',
                minWidth: '200px',
                maxWidth: '200px',
                minHeight: '100px',
                maxHeight: '400px'
              }}
              position={{
                top: '50%',
                left: '50%'
              }}
              font="Amalfi Coast"
              fontSize={21}
              lineHeight={1.7}
              fontWeight="600"
              maxLength={40}
              maxLines={4}
              style={{
                transform: 'translate(-50%, -50%)'
              }}
              value={getTextInput('cardCustomMessage.front')}
              setValue={((value) => setTextInput('cardCustomMessage.front', value))}
            />
          </Visualiser>
        )}

        {activeIndex === 1 && (
          <VisualiserGroup>
            <Visualiser type="GREETINGS_CARD" background="var(--color-white)" subtitle="Not editable">
              {' '}
            </Visualiser>
            <Visualiser type="GREETINGS_CARD" background="var(--color-white)" subtitle="Tap to edit">
              <VisualiserTextBox
                size={{
                  width: '190px',
                  minWidth: '150px',
                  maxWidth: '190px',
                  minHeight: '50px',
                  maxHeight: '260px'
                }}
                position={{
                  top: '50%',
                  left: '50%'
                }}
                maxLength={150}
                maxLines={12}
                lineHeight={1.2}
                fontSize={16}
                style={{
                  transform: 'translate(-50%, -50%)'
                }}
                value={getTextInput('cardCustomMessage.inside')}
                setValue={((value) => setTextInput('cardCustomMessage.inside', value))}
              />
            </Visualiser>
          </VisualiserGroup>
        )}

        {activeIndex === 2 && (
          <Visualiser type="GREETINGS_CARD" background="var(--color-white)" subtitle="Not editable">
            {' '}
          </Visualiser>
        )}
      </div>

      {activeIndex === 0 && (
        <Chooser title="Suggested Messages" open={chooserOpen} onToggle={setChooserOpen}>
          <div className={styles['suggested-messages--container']}>
            <div className={styles['suggested-messages--column']}>
              {suggestedMessages.map((group, index) => {
                const isSelected = selectedSuggestionGroup === index;
                return (
                  <div
                    key={group.id}
                    className={`${styles['suggested-messages--item']} ${isSelected ? styles['suggested-messages--item-selected'] : ''}`}
                    onClick={() => setSelectedSuggestionGroup(index)}
                  >
                    <p>{group.name}</p>
                  </div>
                );
              })}
            </div>
            <div className={styles['suggested-messages--column']} style={{ marginLeft: '20px' }}>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {suggestionList.map((suggestion) => {
                  return (
                    <li key={suggestion} style={{ padding: '10px 0' }}>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setTextInput('cardCustomMessage.front', suggestion);
                          setChooserOpen(false);
                        }}
                      >
                        {suggestion}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </Chooser>
      )}
    </div>
  );
}
